import { useMemo } from 'react';
import { categoryCards, navItems, productsByPage, wideStoreLayoutPageIds } from '../catalog';
import ProductCard from './ProductCard';
import heroImg from '../images/hero.png';

export default function MainContent({ activePage, setActivePage }) {
  const activeProducts = useMemo(() => productsByPage[activePage] || [], [activePage]);
  const useWideStoreLayout = wideStoreLayoutPageIds.includes(activePage);

  const renderProducts = () => {
    const navMeta = navItems.find((item) => item.id === activePage);
    return (
    <section className="products-section">
      <div className="section-header">
        <h2
          className={`section-title${navMeta?.headingImage ? ' section-title--has-img' : ''}`}
        >
          {navMeta?.headingImage ? (
            <img
              src={navMeta.headingImage}
              alt={navMeta.label}
              className="section-heading-img"
            />
          ) : (
            navMeta?.label
          )}
        </h2>
      </div>

      <div className="products-grid">
        {activeProducts.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </section>
    );
  };

  const renderHome = () => (
    <section className="home-section">
      <div className="hero hero-full-banner">
        <img
          src={heroImg}
          alt="Pranavi's Kitchen banner"
          className="hero-full-image"
        />
      </div>

      <section className="products-section home-products-section">
        <div className="section-header explore-header">
          <h2 className="explore-title">
            <span className="leaf" aria-hidden="true">🌿</span>
            Explore Our Products
            <span className="leaf" aria-hidden="true">🌿</span>
          </h2>
        </div>

        <div className="products-grid category-products-grid">
          {categoryCards.map((card) => (
            <button
              type="button"
              className="category-card product-style-card"
              key={card.id}
              onClick={() => setActivePage(card.id)}
            >
              <div className="category-card-photo-wrap">
                <img
                  src={card.image}
                  alt={card.title}
                  className="category-card-photo"
                />
              </div>
              <span className="category-card-title">{card.title}</span>
            </button>
          ))}
        </div>
      </section>
    </section>
  );

  const renderAbout = () => {
    const aboutNav = navItems.find((item) => item.id === 'about');
    return (
    <section className="text-page">
      <h2 className="section-title section-title--has-img">
        {aboutNav?.headingImage ? (
          <img
            src={aboutNav.headingImage}
            alt="About Pranavi's Kitchen"
            className="section-heading-img"
          />
        ) : (
          <>About Pranavi&apos;s Kitchen</>
        )}
      </h2>

      <p>
        Welcome to <strong>Pranavi&apos;s Kitchen</strong> – where tradition meets taste. We specialize in preparing authentic
        homemade pickles, spice powders, and podis using time-tested recipes passed down through generations.
      </p>

      <p>
        Our products are made with carefully selected ingredients, ensuring freshness, purity, and rich flavor in every bite.
        We believe in delivering the true taste of homemade food straight to your kitchen.
      </p>

      <ul>
        <li>✔ 100% Homemade & Hygienic Preparation</li>
        <li>✔ Authentic Traditional Recipes</li>
        <li>✔ No Artificial Preservatives</li>
        <li>✔ Freshly Prepared in Small Batches</li>
        <li>✔ Rich Taste & Quality Guaranteed</li>
      </ul>

      <p>
        Whether it&apos;s spicy pickles, aromatic podis, or flavorful masalas, Pranavi&apos;s Kitchen brings you the comfort of
        homemade food with every order.
      </p>

      <p>
        <strong>Made with love. Served with tradition.</strong>
      </p>
    </section>
    );
  };

  const renderTerms = () => (
    <section className="text-page">
      <h2 className="section-title">Terms & Conditions</h2>

      <ul>
        <li>
          <strong>Order Confirmation:</strong> Orders are confirmed only after successful payment and product availability.
        </li>

        <li>
          <strong>Pricing:</strong> All prices are subject to change without prior notice depending on raw material cost and
          availability.
        </li>

        <li>
          <strong>Delivery:</strong> Delivery timelines may vary based on location, demand, and logistics conditions.
        </li>

        <li>
          <strong>Shipping Charges:</strong> Delivery charges may apply depending on order value and location.
        </li>

        <li>
          <strong>No Cancellation:</strong> Once an order is placed and confirmed, it cannot be cancelled.
        </li>

        <li>
          <strong>No Returns:</strong> Food items are non-returnable and non-refundable due to hygiene reasons.
        </li>

        <li>
          <strong>Damaged Products:</strong> If the product is damaged during delivery, please report within 24 hours with proof
          (photo/video).
        </li>

        <li>
          <strong>Storage Instructions:</strong> Customers must store products as instructed to maintain freshness and shelf
          life.
        </li>

        <li>
          <strong>Shelf Life:</strong> Products are best consumed within the recommended period mentioned on packaging.
        </li>

        <li>
          <strong>Quality Assurance:</strong> We ensure hygienic preparation, but slight variations in taste or color may occur
          as products are homemade.
        </li>

        <li>
          <strong>Allergy Disclaimer:</strong> Products may contain ingredients like peanuts, spices, and oils. Please check
          before consumption.
        </li>

        <li>
          <strong>Liability:</strong> Pranavi&apos;s Kitchen is not responsible for any misuse, improper storage, or allergic
          reactions.
        </li>

        <li>
          <strong>Contact Support:</strong> For any issues or queries, customers can contact us through the provided contact
          details.
        </li>
      </ul>

      <p>
        By placing an order with <strong>Pranavi&apos;s Kitchen</strong>, you agree to all the above terms and conditions.
      </p>
    </section>
  );

  const content = () => {
    if (activePage === 'home') return renderHome();
    if (activePage === 'about') return renderAbout();
    if (activePage === 'terms') return renderTerms();
    return renderProducts();
  };

  return (
    <main
      className={`main-shell${useWideStoreLayout ? ' main-shell--wide-store' : ''}`}
    >
      {content()}
    </main>
  );
}